<template>
  <div v-if="protocol">
    <breadcrumb :items="breadcrumbs"></breadcrumb>
    <div class="d-flex flex-column px-4 mb-4">
      <div>
        <div class="d-flex flex-wrap align-center justify-space-between">
          <h1 class="text-h5 font-weight-bold">{{ $t('esgPlatform.validation.validateData') }}</h1>
          <v-btn color="primary" @click="$router.go(-1)">{{ $t('back') }}</v-btn>
        </div>
        <ProtocolInformation :show-actions="showActions" :protocol="protocol" :sub-organization-id="subOrganizationId || farmerId" />
      </div>
    </div>
    <v-container fluid>
      <div v-for="(category) in categories" :key="category.title" class="mb-8">
        <v-card elevation="0" :class="getCategoryClass(category.title)">
          <v-card-text>
            <div class="d-flex align-center mb-4">
              <div class="text-h6 font-weight-bold">{{ getCategory(category.title) }}</div>
              <v-spacer></v-spacer>
              <span class="text-h6 font-weight-bold">{{ category.totalValidations }} {{
                $t('esgPlatform.validation.dataValidations') }}</span>
            </div>
            <v-row class="d-flex align-center justify-space-between pa-3">
              <div class="d-flex align-center mb-5 " style="gap: 12px; align-items: flex-end !important;">
                <div>
                  <div class="caption mb-1" style="color: #117443; font-weight: 600; visibility: hidden;">{{ $t('esgPlatform.validation.search') }}</div>
                  <v-text-field  v-model="category.searchTerm" prepend-inner-icon="mdi-magnify"
                :label="$t('esgPlatform.validation.search')" outlined dense hide-details style="width: 200px; background-color: white;"
                  ></v-text-field>
                </div>
                <div>
                  <div class="caption mb-1 fw black--text" >
                    {{ $t('esgPlatform.status') }} <v-icon x-small color="#117443">mdi-information</v-icon>
                  </div>
                  <v-select
                    v-model="filterStatus"
                    :items="statusOptions"
                    item-text="label"
                    item-value="value"
                    :placeholder="$t('esgPlatform.validation.select')"
                    outlined dense hide-details clearable
                    style="width: 200px; background-color: white;"
                    @change="onGlobalFilterChange"
                  ></v-select>
                </div>
                  <v-icon size="42" color="#14C27E" >mdi-calendar-month</v-icon>
              </div>
              <div class="d-flex align-center">
                <span class="mr-2">{{ category.currentPage }} of {{ Math.ceil(category.totalValidations /
                  itemsPerPageOptions[category.itemsPerPageIndex]) }}</span>
                <v-btn class="mx-2" fab small outlined color="primary" @click="prevPage(category)"><v-icon
                    dark>mdi-chevron-left</v-icon></v-btn>
                <v-btn class="mx-2" fab small outlined color="primary" @click="nextPage(category)"><v-icon
                    dark>mdi-chevron-right</v-icon></v-btn>
                <v-menu v-model="category.menu" :close-on-content-click="false" :nudge-width="200" left
                  :min-width="417">
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn class="mx-2" fab small outlined color="primary" v-bind="attrs" v-on="on"><v-icon
                        dark>mdi-cog-outline</v-icon></v-btn>
                  </template>
                  <v-card width="417">
                    <v-list>
                      <v-list-item><v-list-item-content><v-list-item-title>{{ $t("deforestation.customization")
                      }}</v-list-item-title></v-list-item-content></v-list-item>
                      <v-divider></v-divider>
                      <v-list-item><v-list-item-content>
                          <div class="pl-3 pt-2 font-weight-normal">{{ $t("deforestation.recordsPerpage") }}</div>
                          <v-slider v-model="category.itemsPerPageIndex" :tick-labels="itemsPerPageOptions" :max="3"
                            step="1" ticks="always" tick-size="4"></v-slider>
                        </v-list-item-content></v-list-item>
                      <v-divider></v-divider>
                      <v-list-item><v-list-item-content><v-list-item-title>{{ $t("deforestation.visibleColumns")
                      }}</v-list-item-title></v-list-item-content></v-list-item>
                      <v-list-item><v-row><v-col class="mt-n9" cols="6" v-for="header in headers"
                            :key="header.value"><v-checkbox v-if="header.text" v-model="category.selectedHeaders"
                              :label="header.text" color="primary" :value="header.value"
                              hide-details></v-checkbox></v-col></v-row></v-list-item>
                      <v-divider></v-divider>
                    </v-list>
                    <v-card-actions class="mb-5 justify-center">
                      <v-btn outlined color="primary" @click="resetDefaut(category)" width="190"><span
                          class="text-truncate" style="max-width:150px">{{ $t("deforestation.restoreDefault")
                          }}</span></v-btn>
                      <v-btn color="primary" @click="resetTableStructure(category)" width="190">{{
                        $t("deforestation.apply") }}</v-btn>
                    </v-card-actions>
                  </v-card>
                </v-menu>
              </div>
            </v-row>
            <v-card class="mb-4 font-weight-bold">
              <v-row class="white--text py-2 px-4 ma-0" style="background: #117443;">
                <v-col cols="3" v-if="category.selectedHeaders.includes('inputData')">{{
                  $t('esgPlatform.validation.inputData')
                }}</v-col>
                <v-col cols="2" v-if="category.selectedHeaders.includes('submittedBy')">
                  {{ $t('esgPlatform.submittedBy') }}
                </v-col>
                <v-col cols="5" v-if="category.selectedHeaders.includes('issueGoal')">{{
                  $t('esgPlatform.validation.issueAndGoal')
                }}</v-col>
                <v-col cols="2" v-if="category.selectedHeaders.includes('status')">
                  {{ $t('esgPlatform.status') }}
                </v-col>
              </v-row>
            </v-card>
            <div v-for="(item, index) in category.items" :key="index">
              <v-card elevation="2" class="mb-4">
                <v-card-text class="pa-4">
                  <v-row align="center">
                     <!-- Input Data Column -->
                    <v-col cols="3" v-if="category.selectedHeaders.includes('inputData')" @click="handleGlobalDownload">
                      <div v-if="item.type === 'actionPlan'">
                        <div class="mt-1" v-html="formatResponse(item)"></div>
                      </div>
                      <div v-else-if="item.type === 'getData'">
                        <span class="font-weight-bold">{{ cleanTitle(item.response.title) }}:</span>
                        <div class="mt-1" v-html="formatResponse(item)"></div>
                      </div>
                      <div v-else>
                        <span v-html="formatResponse(item)"></span>
                      </div>
                    </v-col>
                    <!-- Submitted By Column -->
                    <v-col cols="2" v-if="category.selectedHeaders.includes('submittedBy')">
                      <div class="font-weight-bold black--text">{{ item.submittedByName }}</div>
                      <div class="text-capitalize grey--text text--darken-1">{{ item.submittedByType }}</div>
                      <div class="caption grey--text">{{ formatDate(item.createdAt) }}</div>
                    </v-col>

                    <!-- Topic & Goal Column -->
                    <v-col cols="5" v-if="category.selectedHeaders.includes('issueGoal')">
                      <div class="font-weight-bold black--text">{{ item.issue.title }}</div>
                      <div class="font-weight-medium">{{ item.goal.title }}</div>
                      <div class="mt-5">
                        <span class="font-weight-bold black--text">{{ $t('esgPlatform.questions') }} {{ item.index }}</span>
                      </div>
                      <div>
                        <span class="font-weight-medium">{{ item.question.title }}</span>
                      </div>
                      <a class="primary--text text-decoration-none" style="cursor:pointer" @click.prevent="openQuestionDialog(item)">
                        {{ $t('esgPlatform.validation.viewResponse') }}
                      </a>
                    </v-col>

                    <v-col cols="2" class="" v-if="category.selectedHeaders.includes('status')">
                      <div class="final-status-box">
                        <div class="final-status-title">{{ $t('esgPlatform.validation.finalStatus') }}</div>

                        <!-- Approved -->
                        <template v-if="item.approveReason">
                          <div class="status-indicator">
                            <v-icon color="#10b981" size="36">mdi-check-decagram</v-icon>
                            <span class="status-label">{{ $t('esgPlatform.approved') }}</span>
                          </div>
                          <div class="status-comments">
                            <div class="status-comments-title">{{ $t('esgPlatform.validation.comments') }}:</div>
                            <div class="status-comments-text">{{ item.approveReason }}</div>
                          </div>
                        </template>

                        <!-- Rejected -->
                        <template v-else-if="item.rejectionReason">
                          <div class="status-indicator">
                            <v-icon color="#ef4444" size="36">mdi-cancel</v-icon>
                            <span class="status-label">{{ $t('esgPlatform.rejected') }}</span>
                          </div>
                          <div class="status-comments">
                            <div class="status-comments-title">{{ $t('esgPlatform.validation.comments') }}:</div>
                            <div class="status-comments-text">{{ item.rejectionReason }}</div>
                          </div>
                        </template>

                        <!-- Pending -->
                        <template v-else>
                          <div class="status-indicator">
                            <v-icon color="#9ca3af" size="36">mdi-progress-clock</v-icon>
                            <span class="status-label">{{ $t('esgPlatform.pending') }}</span>
                          </div>
                        </template>

                        <!-- Pending: show APPROVE / REJECT buttons -->
                        <template v-if="!item.approveReason && !item.rejectionReason">
                          <div v-if="!isSubEnterprise || isFarmerMode" class="status-action-row mt-2">
                            <v-btn depressed class="action-btn approve-btn" @click="openRejectDialog(item, 'approve')">
                              {{ $t('esgPlatform.validation.approveAction') }}
                            </v-btn>
                            <v-btn depressed class="action-btn reject-btn" @click="openRejectDialog(item, 'reject')">
                              {{ $t('esgPlatform.validation.rejectAction') }}
                            </v-btn>
                          </div>
                        </template>

                        <!-- Approved/Rejected: show UPDATE STATUS button -->
                        <template v-else>
                          <div v-if="!isSubEnterprise || isFarmerMode" class="mt-2">
                            <v-btn depressed class="action-btn update-btn" @click="openRejectDialog(item, item.approveReason ? 'approve' : 'reject')">
                              {{ $t('esgPlatform.updateStatus') }}
                            </v-btn>
                          </div>
                        </template>
                      </div>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </div>
          </v-card-text>
        </v-card>
      </div>
      <ValidationConfirm :dialog="showDialog" :item="selectedItem" @cancel="showDialog = false" @confirm="handleConfirm" />
      <QuestionDialog :dialog="showQuestionDialog" :question-data="selectedQuestion" :answer-data="selectedAnswer"
        @close="showQuestionDialog = false" />
    </v-container>
  </div>
</template>
<script>
import ValidationConfirm from './ValidationConfirm.vue';
import QuestionDialog from './QuestionDialog.vue';
import EsgService from "@/_services/EsgService";
import ProtocolInformation from '../../dashboard/components/protocol/sub-organization/ProtocolInformation.vue';
import LoadingMixin from "@/mixins/LoadingMixin";
import { mapGetters } from 'vuex';

export default {
  components: { ValidationConfirm, QuestionDialog, ProtocolInformation },
  mixins: [LoadingMixin],
  data() {
    return {
      breadcrumbs: [
        { text: this.$t('esgPlatform.esgPlatform'), disabled: false, to: { name: 'eudrDashboard' } },
        { text: this.$t('dashboard'), link: true, exact: true, disabled: true },
      ],
      showDialog: false,
      protocol: null,
      subOrganizationId: this.$route.params.subOrganizationId,
      farmerId: this.$route.params.farmerId || this.$route.query.farmerId,
      userType: this.$route.query.userType,
      categories: [
        { title: 'Environmental', items: [], searchTerm: '', currentPage: 1, totalValidations: 0, itemsPerPageIndex: 1, menu: false, selectedHeaders: ['inputData', 'submittedBy', 'issueGoal','status'] },
        { title: 'Social', items: [], searchTerm: '', currentPage: 1, totalValidations: 0, itemsPerPageIndex: 1, menu: false, selectedHeaders: ['inputData', 'submittedBy', 'issueGoal','status'] },
        { title: 'Governance', items: [], searchTerm: '', currentPage: 1, totalValidations: 0, itemsPerPageIndex: 1, menu: false, selectedHeaders: ['inputData', 'submittedBy', 'issueGoal','status'] },
      ],
      filterUserId: null,
      filterStatus: null,
      itemsPerPageOptions: [5, 10, 25, 50],
      debounceTimeouts: {
        Environmental: null,
        Social: null,
        Governance: null,
      },
      selectedResponseId: null,
      selectedItem: null,
      showActions: false,
      showQuestionDialog: false,
      selectedQuestion: null,
      selectedAnswer: null,
      selectedFarmResponses: [],
    };
  },
  computed: {
    ...mapGetters(['isSubEnterprise']),
    isFarmerMode() {
      return this.userType === 'farmer' && !!this.farmerId;
    },
    statusOptions() {
      return [
        { label: this.$t('esgPlatform.pending'), value: 'pending' },
        { label: this.$t('esgPlatform.approved'), value: 'approved' },
        { label: this.$t('esgPlatform.rejected'), value: 'rejected' },
      ];
    },
    headers() {
      return [
        { text: this.$t('esgPlatform.validation.inputData'), value: 'inputData' },
        { text: this.$t('esgPlatform.submittedBy'), value: 'submittedBy' },
        { text: this.$t('esgPlatform.validation.issueAndGoal'), value: 'issueGoal' },
        { text: this.$t('esgPlatform.status'), value: 'status' },
      ];
    }
  },
  mounted() {
    this.fetchProtocolDetail();
    this.fetchAllData();
  },
  watch: {
    'categories.0.searchTerm'(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.debounceFetchCategoryData(this.categories[0]);
      }
    },
    'categories.1.searchTerm'(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.debounceFetchCategoryData(this.categories[1]);
      }
    },
    'categories.2.searchTerm'(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.debounceFetchCategoryData(this.categories[2]);
      }
    }
  },
  methods: {
    formatDate(dateStr) {
      if (!dateStr) return '';
      const d = new Date(dateStr);
      const locale = this.$i18n?.locale || 'en';
      const day = String(d.getDate()).padStart(2, '0');
      const month = d.toLocaleString(locale, { month: 'short' }).replace('.', '').toUpperCase();
      const year = d.getFullYear();
      const hours = String(d.getHours()).padStart(2, '0');
      const minutes = String(d.getMinutes()).padStart(2, '0');
      const seconds = String(d.getSeconds()).padStart(2, '0');
      return `${day}-${month}-${year} ${hours}:${minutes}:${seconds}`;
    },
    onGlobalFilterChange() {
      this.categories.forEach(c => { c.currentPage = 1; });
      this.fetchAllData();
    },
    debounceFetchCategoryData(category) {
      if (this.debounceTimeouts[category.title]) {
        clearTimeout(this.debounceTimeouts[category.title]);
      }
      this.debounceTimeouts[category.title] = setTimeout(() => {
        category.currentPage = 1;
        this.fetchCategoryData(category);
      }, 300);
    },
    async fetchProtocolDetail() {
      this.startLoading();
      try {
        const protocolId = this.$route.params.id;
        const submittedByType = this.isFarmerMode ? 'farmer' : 'sub-organization';
        const submittedById = this.isFarmerMode ? this.farmerId : this.subOrganizationId;

        if (submittedById) {
          const response = await EsgService.getEsgProgressForRespondent(submittedByType, submittedById, protocolId);
          this.protocol = response.data;
        } else {
          const response = await EsgService.getEsgStandardDetails(protocolId);
          this.protocol = response.data;
        }
      } finally {
        this.stopLoading();
      }
    },

    groupFarmerResponses(items) {
      if (!this.isFarmerMode) return items;

      return items.map(item => ({
        ...item,
        farmResponses: [{
          responseId: item.responseId,
          farmId: item.farmIds,
          farmName: item.farmName,
          response: item.response,
          rejectionReason: item.rejectionReason,
          rejectedByUserId: item.rejectedByUserId,
          _id: item._id
        }]
      }));
    },
    async fetchAllData() {
      this.startLoading();
      const protocolId = this.$route.params.id;
      const promises = this.categories.map(category =>
        EsgService.getValidationData(protocolId, {
          type: category.title,
          page: category.currentPage,
          limit: this.itemsPerPageOptions[category.itemsPerPageIndex],
          searchTerm: category.searchTerm,
          ...(this.isFarmerMode && { userType: 'farmer', farmerId: this.farmerId }),
          subOrganizationId: this.isFarmerMode ? undefined : this.subOrganizationId,
          filterStatus: this.filterStatus,
        })
      );
      try {
        const responses = await Promise.all(promises);
        responses.forEach((response, index) => {
          const category = this.categories[index];
          if (response.success) {
            const allItems = response.data.info.info || [];
            // const filteredItems = allItems.filter(item => item.rejectionReason == null);
            category.items = this.groupFarmerResponses(allItems);
            category.totalValidations = response.data.info.total || 0;
          } else {
            console.error(`Error fetching data for ${category.title}:`, response.message);
            category.items = [];
          }
        });
      } catch (error) {
        console.error('API error:', error);
        this.categories.forEach(category => { category.items = []; });
      } finally {
        this.stopLoading();
      }
    },
    formatResponse(item) {
      if (item.type === 'actionPlan') {
        let result = `<span class="font-weight-bold">${item.response.title || this.$t('esgPlatform.validation.noTitle')}</span>`;
        if (item.response.files?.length) {
          const filesHtml = item.response.files.map(file => this.formatFileWithDownload(file)).join('<br>');
          result += `<div>${filesHtml}</div>`;
        }
        return result;
      } else if (item.type === 'getData') {
        const value = item.response?.value;
        if (!value && value !== 0) return this.$t('esgPlatform.validation.noData');
        if (typeof value === 'string' || typeof value === 'number') {
          return value.toString();
        } else if (Array.isArray(value)) {
          if (value.length && typeof value[0] !== 'object') {
            return value.join('<br>');
          }
          // Attach File array — use formatFileWithDownload for consistent icon styling
          return value.map(file => this.formatFileWithDownload(file)).join('<br>');
        } else if (typeof value === 'object' && value !== null) {
          if (value.id && value.abbr && 'value' in value) {
            return `${value.value} ${value.abbr}`;
          } else if (value.key || value.fileUrl || value.attachmentLink) {
            // E-sign or single file — use formatFileWithDownload for consistent icon styling
            return this.formatFileWithDownload({
              fileUrl: value.fileUrl || value.attachmentLink,
              fileName: value.fileName,
              key: value.key
            });
          }
          return JSON.stringify(value);
        }
        return this.$t('esgPlatform.validation.noData');
      } else if (item.type === 'dimitraModule' || item.type === 'dimitraModuleFromOption') {
        return `<span class="font-weight-bold">${this.$t('esgPlatform.validation.dimitraModule')}</span>`;
      } else {
        // Main response
        const type = item.question.questionType;
        const response = item.response || {};
        switch (type) {
          case 'text-field':
          case 'text-area':
          case 'numeric':
            return response.value ?? this.$t('esgPlatform.validation.noResponse');
          case 'radio-button': {
            const selected = response.value;
            if (!selected) return this.$t('esgPlatform.validation.noSelection');
            return selected.label || this.$t('esgPlatform.validation.noSelection');
          }
          case 'check-boxes': {
            const values = Array.isArray(response.value) ? response.value : (response.value ? [response.value] : []);
            if (!values.length) return this.$t('esgPlatform.validation.noSelection');
            return values.map(sel => sel?.label || this.$t('esgPlatform.validation.unknownOption')).join(', ');
          }
          case 'measurement-unit':
            return response.value ? `${response.value.value || ''} ${response.value.abbr || ''}` : this.$t('esgPlatform.validation.noMeasurement');
          case 'attach-files':
            if (Array.isArray(response.value)) {
              // use formatFileWithDownload for consistent icon styling
              return response.value.map(file => this.formatFileWithDownload(file)).join('<br>');
            }
            return this.$t('esgPlatform.validation.noFile');
          case 'digital-signature':
            if (response.value?.attachmentLink) {
              // use formatFileWithDownload for consistent icon styling
              return this.formatFileWithDownload({
                fileUrl: response.value.attachmentLink,
                fileName: 'signature.png',
                key: response.value.key
              });
            }
            return this.$t('esgPlatform.validation.noSignature');
          case 'get-data':
            return this.$t('esgPlatform.validation.noData');
          case 'dimitra-module':
            return `<span class="font-weight-bold">${this.$t('esgPlatform.validation.dimitraModule')}</span>`;
          default:
            return this.$t('esgPlatform.validation.unknownType');
        }
      }
    },
    getFileInfo(file) {
      const url = file.fileUrl || `https://dimitra-pre-prod-public-images.s3.amazonaws.com/${file.key}`;
      const name = file.fileName || file.key || this.$t('esgPlatform.validation.unnamedFile');
      return { url, name };
    },
    formatFile(file) {
      const { url, name } = this.getFileInfo(file);
      return this.isImage(name)
        ? `<img src="${url}" alt="${name}" style="max-width: 100px;" />`
        : `<a href="${url}" target="_blank">${name}</a>`;
    },
    formatFileWithDownload(file) {
      const { url, name } = this.getFileInfo(file);
      const downloadLabel = this.$t('esgPlatform.validation.download');
      const downloadBtn = `<a href="#" class="force-download" data-url="${url}" data-name="${name}" title="${downloadLabel}" style="color:#117443;text-decoration:none;"><i class="mdi mdi-download"></i></a>`;
      const content = this.isImage(name)
        ? `<img src="${url}" alt="${name}" style="max-width:100px;" />`
        : `<a href="${url}" target="_blank">${name}</a>`;
      return `<div style="display:inline-flex;align-items:center;gap:6px;">${content}${downloadBtn}</div>`;
    },
    isImage(fileString) {
      const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.svg', '.webp'];
      const path = (fileString || '').split('?')[0];
      const ext = path.slice(path.lastIndexOf('.')).toLowerCase();
      return imageExtensions.includes(ext);
    },
    cleanTitle(title) {
      return title.replace(/\s*\[.*\]\s*$/, '').trim();
    },
    nextPage(category) {
      const totalPages = Math.ceil(category.totalValidations / this.itemsPerPageOptions[category.itemsPerPageIndex]);
      if (category.currentPage < totalPages) {
        category.currentPage++;
        this.fetchCategoryData(category);
      }
    },
    prevPage(category) {
      if (category.currentPage > 1) {
        category.currentPage--;
        this.fetchCategoryData(category);
      }
    },
    async fetchCategoryData(category) {
      const protocolId = this.$route.params.id;
      try {
        const response = await EsgService.getValidationData(protocolId, {
          type: category.title,
          page: category.currentPage,
          limit: this.itemsPerPageOptions[category.itemsPerPageIndex],
          searchTerm: category.searchTerm,
          ...(this.isFarmerMode && { userType: 'farmer', farmerId: this.farmerId }),
          subOrganizationId: this.isFarmerMode ? undefined : this.subOrganizationId,
          filterStatus: this.filterStatus,
        });
        if (response.success) {
          const allItems = response.data.info.info || [];
          // const filteredItems = allItems.filter(item => item.rejectionReason == null);
          category.items = this.groupFarmerResponses(allItems);
          category.totalValidations = response.data.info.total || 0;
        } else {
          console.error(`Error fetching data for ${category.title}:`, response.message);
          category.items = [];
        }
      } catch (error) {
        console.error('API error:', error);
        category.items = [];
      }
    },
    async resetDefaut(category) {
      category.itemsPerPageIndex = 1;
      category.selectedHeaders = ['inputData', 'submittedBy', 'issueGoal', 'status'];
      await this.fetchCategoryData(category);
      category.menu = false;
    },
    async resetTableStructure(category) {
      await this.fetchCategoryData(category);
      category.menu = false;
    },
    getCategoryClass(title) {
      const classes = { 'Environmental': 'environmental-card', 'Social': 'social-card', 'Governance': 'governance-card' };
      return classes[title] || '';
    },
    getCategory(title) {
      if (title === 'Environmental') return this.$t('esgPlatform.validation.environmental');
      if (title === 'Social') return this.$t('esgPlatform.validation.social');
      if (title === 'Governance') return this.$t('esgPlatform.validation.governance');
    },
    openRejectDialog(item) {
      this.selectedItem = item;
      // For items with multiple farm responses, we'll handle rejection in the dialog
      if (item.farmResponses && item.farmResponses.length > 1) {
        this.openQuestionDialog(item);
      } else {
        this.selectedResponseId = item.responseId;
        this.showDialog = true;
      }
    },
    handleGlobalDownload(event) {
      const btn = event.target.closest('.force-download');
      if (btn) {
        event.preventDefault();
        this.forceFileDownload(btn.dataset.url, btn.dataset.name);
      }
    },
    forceFileDownload(url, filename) {
      if (!url) return;
      if (this.isImage(filename || url)) {
        const img = new Image();
        img.crossOrigin = 'anonymous'; // Request CORS — works if S3 allows it
        img.onload = () => {
          const canvas = document.createElement('canvas');
          canvas.width = img.naturalWidth;
          canvas.height = img.naturalHeight;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0);
          canvas.toBlob((blob) => {
            if (blob) {
              const blobUrl = window.URL.createObjectURL(blob);
              const link = document.createElement('a');
              link.href = blobUrl;
              link.download = filename || this.$t('esgPlatform.validation.download');
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              window.URL.revokeObjectURL(blobUrl);
            } else {
              // Canvas blob failed — fallback to new tab
              window.open(url, '_blank');
            }
          });
        };
        img.onerror = () => {
          // crossOrigin failed — fallback to new tab
          window.open(url, '_blank');
        };
        img.src = url + (url.includes('?') ? '&' : '?') + 't=' + Date.now(); // cache-bust
      } else {
        window.open(url, '_blank');
      }
    },
    // async forceFileDownload(url, filename) {
    //   if (!url) return;
    //   try {
    //     const response = await fetch(url);
    //     const blob = await response.blob();
    //     const link = document.createElement('a');
    //     link.href = window.URL.createObjectURL(blob);
    //     link.download = filename || 'download';
    //     document.body.appendChild(link);
    //     link.click();
    //     document.body.removeChild(link);
    //     window.URL.revokeObjectURL(link.href);
    //   } catch (error) {
    //     console.error('Download failed', error);
    //     window.open(url, '_blank'); // fallback open in tab
    //   }
    // },
    openQuestionDialog(item) {
      // Use the specific item that was clicked instead of searching for it
      console.log("item", item);
      console.log("item.response", item.response);

      // Map the data structure to match what QuestionDialog expects
      this.selectedQuestion = {
        ...item.question,
        heading: item.heading,
        issue: item.issue,
        goal: item.goal,
        assessment: item.assessment,
        order: item.question.order,
        questionType: item.question.questionType,
        title: item.question.title,
        description: item.question.description || '',
        score: item.question.score,
        index: item.index,
        respondentType: item.question.respondentType
      };
      this.selectedAnswer = item;
      this.selectedFarmResponses = item.farmResponses || [];
      this.showQuestionDialog = true;
    },
    handleConfirm({ approveReason, rejectionReason }) {
      if (this.selectedResponseId) {
        this.confirmRejection(this.selectedResponseId, { approveReason, rejectionReason });
      }
    },
    async confirmRejection(responseId, { approveReason, rejectionReason }) {
      try {
        const payload = approveReason
          ? { esgAssessmentResponseId: responseId, approveReason }
          : { esgAssessmentResponseId: responseId, rejectionReason };

        if (!approveReason && !rejectionReason) {
          this.$toast.error(this.$t('esgPlatform.validation.reasonRequired'));
          return;
        }
        this.startLoading();
        await EsgService.validateResponse(payload);
        this.$notify({
          title: approveReason
            ? this.$t('esgPlatform.validation.approvalSuccess')
            : this.$t('esgPlatform.validation.rejectionSuccess'),
          type: "success",
        });
        await this.fetchAllData();
      } catch (error) {
        console.error('Error confirming:', error);
        this.$notify({
          title: approveReason
            ? this.$t('esgPlatform.validation.approvalFailed')
            : this.$t('esgPlatform.validation.rejectionFailed'),
          type: 'error',
        });
      } finally {
        this.showDialog = false;
        this.selectedResponseId = null;
        this.selectedItem = null;
        this.stopLoading();
      }
    },
  }
}
</script>

<style scoped>
.environmental-card {
  background: #E4FFF2;
}

.social-card {
  background: #EFFDFF;
}

.governance-card {
  background: #FFF4E8;
}

.final-status-box {
  border: 1.5px solid #d1d5db;
  border-radius: 10px;
  padding: 12px 14px;
  width: 180px;
  min-width: 180px;
  max-width: 180px;
  background: #ffffff;
  box-shadow: 0 1px 4px rgba(0,0,0,0.06);
}

.final-status-title {
  font-size: 13px;
  font-weight: 700;
  color: #111827;
  margin-bottom: 10px;
}

/* Icon + label stacked centred */
.status-indicator {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  margin-bottom: 4px;
}

.status-label {
  font-size: 14px;
  font-weight: 600;
  color: #374151;
}

/* Comments block */
.status-comments {
  margin-top: 8px;
}
.status-comments-title {
  font-size: 12px;
  font-weight: 700;
  color: #111827;
  margin-bottom: 2px;
}
.status-comments-text {
  font-size: 11px;
  color: #4b5563;
  word-break: break-word;
  white-space: normal;
  line-height: 1.5;
}

/* Button row */
.status-action-row {
  display: flex;
  gap: 6px;
}

.action-btn {
  flex: 1;
  font-size: 10px !important;
  font-weight: 700 !important;
  letter-spacing: 0.4px;
  height: 28px !important;
  min-height: 28px !important;
  padding: 0 6px !important;
  border-radius: 5px !important;
  text-transform: uppercase;
}

.approve-btn {
  background-color: #10b981 !important;
  color: #ffffff !important;
}

.reject-btn {
  background-color: #ef4444 !important;
  color: #ffffff !important;
}

.update-btn {
  width: 100% !important;
  background-color: #10b981 !important;
  color: #ffffff !important;
}
</style>
